import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { AuthService, OnboardingContextPayload } from '@infodocs/shared/services/auth.service';
import { finalize } from 'rxjs';

const OTHER = 'Other';
const MANAGE_TRUSTS = 'Manage Trusts';
const REFERRAL = 'Recommended by a friend or colleague';

const ROLE_OPTIONS = [
	'Business Owner',
	'Accountant',
	'Company Secretary',
	'Finance Manager',
	'Lawyer',
	OTHER,
] as const;

const COMPANY_COUNT_OPTIONS = ['1', '2-5', '6-20', '21-50', '51-150', '151+'] as const;

const INTENT_OPTIONS = [
	'File Beneficial Ownership',
	'File Annual Returns',
	'New Company Registration',
	'Create Share Certificates',
	'Update Director Information',
	'Manage Company Records',
	'Manage Trusts',
	'Explore InfoDocs',
	OTHER,
] as const;

const ALL_OF_THE_ABOVE = 'All of the above';

const HEAR_OPTIONS = [
	'Search engine (Google, Yahoo, etc.)',
	'AI agent (ChatGPT, Claude, etc.)',
	REFERRAL,
	'Social media',
	'Webinar',
	'Elevate Business Academy (EBAC)',
	'Blog or publication',
	OTHER,
] as const;

function atLeastOne(control: AbstractControl): ValidationErrors | null {
	const value = control.value as string[];
	return value.length > 0 ? null : { required: true };
}

function splitInColumns<T>(options: readonly T[]): readonly [readonly T[], readonly T[]] {
	const midpoint = Math.ceil(options.length / 2);

	return [options.slice(0, midpoint), options.slice(midpoint)];
}

function splitIntentColumns(
	options: readonly string[],
	allOfTheAbove: string,
	other: string,
): readonly [readonly string[], readonly string[]] {
	return [options.slice(0, 5), [...options.slice(5), allOfTheAbove, other]];
}

@Component({
	selector: 'app-onboarding-context-form',
	templateUrl: './onboarding-context-form.component.html',
	styleUrls: ['./onboarding-context-form.component.scss'],
	imports: [ReactiveFormsModule],
})
export class OnboardingContextFormComponent {
	readonly finished = output<void>();

	protected readonly roleColumns = splitInColumns(ROLE_OPTIONS);
	protected readonly companyCountOptions = COMPANY_COUNT_OPTIONS;
	protected readonly trustCountOptions = COMPANY_COUNT_OPTIONS;
	protected readonly intentColumns = splitIntentColumns(
		INTENT_OPTIONS.filter(option => option !== OTHER),
		ALL_OF_THE_ABOVE,
		OTHER,
	);
	protected readonly otherOption = OTHER;
	protected readonly allOfTheAboveOption = ALL_OF_THE_ABOVE;
	protected readonly hearOptions = HEAR_OPTIONS;
	private readonly intentOptionsExceptOther = INTENT_OPTIONS.filter(option => option !== OTHER);
	readonly isSaving = signal(false);
	protected readonly errorMessage = signal('');
	protected readonly form = new FormGroup({
		roles: new FormControl<string[]>([], { nonNullable: true, validators: [atLeastOne] }),
		roleOther: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
		companyCount: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
		trustCount: new FormControl('', { nonNullable: true }),
		intents: new FormControl<string[]>([], { nonNullable: true, validators: [atLeastOne] }),
		intentOther: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
		hear: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
		hearReferral: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
		hearOther: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
	});

	private readonly auth = inject(AuthService);
	private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
	readonly isInvalid = computed(() => this.formStatus() !== 'VALID');

	constructor() {
		const { roles, roleOther, intents, intentOther, hear } = this.form.controls;
		roles.valueChanges
			.pipe(takeUntilDestroyed())
			.subscribe(selected => this.applyFollowUp(roleOther, selected.includes(OTHER)));
		intents.valueChanges
			.pipe(takeUntilDestroyed())
			.subscribe(selected => {
				this.applyFollowUp(intentOther, selected.includes(OTHER));
				this.applyTrustCount(selected.includes(MANAGE_TRUSTS));
			});
		hear.valueChanges
			.pipe(takeUntilDestroyed())
			.subscribe(value => this.syncHearFollowUps(value));
		this.prefillHear(hear);
	}

	protected isRoleSelected(option: string): boolean {
		return this.form.controls.roles.value.includes(option);
	}

	protected isIntentSelected(option: string): boolean {
		return this.form.controls.intents.value.includes(option);
	}

	protected toggleRole(option: string): void {
		this.toggleMultiValue(this.form.controls.roles, option);
	}

	protected isAllIntentsSelected(): boolean {
		const selected = this.form.controls.intents.value;

		return this.intentOptionsExceptOther.every(option => selected.includes(option));
	}

	protected toggleIntent(option: string): void {
		this.toggleMultiValue(this.form.controls.intents, option);
	}

	protected toggleAllIntents(): void {
		const control = this.form.controls.intents;
		const otherSelected = control.value.includes(OTHER);
		const next = this.isAllIntentsSelected()
			? (otherSelected ? [OTHER] : [])
			: [...this.intentOptionsExceptOther, ...(otherSelected ? [OTHER] : [])];

		control.setValue(next);
		control.markAsTouched();
	}

	protected showRoleOther(): boolean {
		return this.form.controls.roles.value.includes(OTHER);
	}

	protected showIntentOther(): boolean {
		return this.form.controls.intents.value.includes(OTHER);
	}

	protected showTrustCount(): boolean {
		return this.form.controls.intents.value.includes(MANAGE_TRUSTS);
	}

	protected showReferral(): boolean {
		return this.form.controls.hear.value === REFERRAL;
	}

	protected showHearOther(): boolean {
		return this.form.controls.hear.value === OTHER;
	}

	protected showFieldError(control: AbstractControl): boolean {
		return control.touched && control.invalid;
	}

	protected save(): void {
		this.form.markAllAsTouched();
		if (this.form.invalid || this.isSaving()) {
			return;
		}

		this.errorMessage.set('');
		this.isSaving.set(true);
		this.auth.saveOnboardingContext(this.payload())
			.pipe(finalize(() => this.isSaving.set(false)))
			.subscribe({
				next: () => this.finished.emit(),
				error: (error: HttpErrorResponse) => this.errorMessage.set(this.apiMessage(error)),
			});
	}

	private payload(): OnboardingContextPayload {
		const value = this.form.getRawValue();

		return {
			roles: value.roles,
			role_other: value.roles.includes(OTHER) ? value.roleOther : null,
			company_count: value.companyCount,
			trust_count: this.showTrustCount() ? value.trustCount : null,
			intents: value.intents,
			intent_other: value.intents.includes(OTHER) ? value.intentOther : null,
			hear: value.hear,
			hear_referral: value.hear === REFERRAL ? value.hearReferral : null,
			hear_other: value.hear === OTHER ? value.hearOther : null,
		};
	}

	private prefillHear(hear: FormControl<string>): void {
		const saved = this.auth.currentUser()?.hear;
		if (!saved || !(HEAR_OPTIONS as readonly string[]).includes(saved)) {
			return;
		}

		hear.setValue(saved);
	}

	private syncHearFollowUps(hear: string): void {
		this.applyOptionalFollowUp(this.form.controls.hearReferral, hear === REFERRAL);
		this.applyFollowUp(this.form.controls.hearOther, hear === OTHER);
	}

	private applyOptionalFollowUp(control: FormControl<string>, visible: boolean): void {
		control.setValidators([Validators.maxLength(255)]);
		if (!visible && control.value !== '') {
			control.setValue('');
		}
		control.updateValueAndValidity();
	}

	private toggleMultiValue(control: FormControl<string[]>, option: string): void {
		const current = control.value;
		const next = current.includes(option)
			? current.filter(item => item !== option)
			: [...current, option];

		control.setValue(next);
		control.markAsTouched();
	}

	private applyTrustCount(required: boolean): void {
		const control = this.form.controls.trustCount;
		control.setValidators(required ? [Validators.required] : []);
		if (!required && control.value !== '') {
			control.setValue('');
		}
		control.updateValueAndValidity();
	}

	private applyFollowUp(control: FormControl<string>, required: boolean): void {
		control.setValidators(required
			? [Validators.required, Validators.maxLength(255)]
			: [Validators.maxLength(255)]);
		if (!required && control.value !== '') {
			control.setValue('');
		}
		control.updateValueAndValidity();
	}

	private apiMessage(error: HttpErrorResponse): string {
		const message = error.error?.message;

		return typeof message === 'string' ? message : 'Something went wrong. Please try again.';
	}
}
